import { useEffect, useRef, useState, type ComponentType } from "react";
import { flushSync } from "react-dom";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import { useT } from "./lib/i18n";
import { landingCopy } from "./lib/landing-copy";

const pageLoaders = {
  "/how-it-works": () => import("./pages/HowItWorks"),
  "/strategies": () => import("./pages/Strategies"),
  "/tokens": () => import("./pages/Tokens"),
};
type PagePath = keyof typeof pageLoaders;
type PageModule = { default: ComponentType };
const pageCache: Partial<Record<PagePath, { component?: ComponentType; promise?: Promise<PageModule>; error?: unknown }>> = {};

function loadPage(pathname: PagePath) {
  const cached = pageCache[pathname] ?? (pageCache[pathname] = {});
  cached.promise ??= pageLoaders[pathname]().then(module => {
    cached.component = module.default;
    return module;
  }).catch(error => { cached.error = error; throw error; });
  return cached.promise;
}

function DeferredPage({ pathname }: { pathname: PagePath }) {
  const cached = pageCache[pathname];
  if (cached?.error) throw cached.error;
  if (!cached?.component) throw loadPage(pathname);
  const Page = cached.component;
  return <Page/>;
}

function preloadPage(pathname: string) {
  // The page boundary handles a failed import when the route is rendered.
  if (Object.prototype.hasOwnProperty.call(pageLoaders, pathname)) return loadPage(pathname as PagePath).catch(() => undefined);
}

type RoutePhase = "idle" | "preparing" | "leaving" | "arriving";

function NotFound() {
  const { lang } = useT();
  const c = landingCopy[lang];
  return <section className="a1-container a1-final"><p className="a1-eyebrow justify-center">404</p><h1 className="text-4xl font-black">{c.notFound}</h1><p className="mt-6">{c.notFoundSub}</p><Link className="a1-button" to="/">{c.home}</Link></section>;
}

export default function App() {
  const requestedLocation = useLocation();
  const [displayedLocation, setDisplayedLocation] = useState(requestedLocation);
  const displayed = useRef(requestedLocation);
  const [routePhase, setRoutePhase] = useState<RoutePhase>("idle");

  useEffect(() => {
    if (requestedLocation.key === displayed.current.key) { setRoutePhase("idle"); return; }
    let cancelled = false;
    let transition: Pick<ViewTransition, "ready" | "finished" | "skipTransition"> | undefined;
    let timer = 0;
    let frame = 0;
    const commit = (phase: RoutePhase = "idle") => {
      if (cancelled) return;
      displayed.current = requestedLocation;
      setDisplayedLocation(requestedLocation);
      setRoutePhase(phase);
    };
    const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Hash navigation stays within the page; it does not need a page crossfade.
    if (requestedLocation.pathname === displayed.current.pathname) {
      commit();
      return;
    }

    setRoutePhase("preparing");
    const navigate = async () => {
      // Keep the current page until the destination's code is ready.
      await preloadPage(requestedLocation.pathname);
      if (cancelled) return;
      if (reducedMotion() || document.hidden) { commit(); return; }

      if (typeof document.startViewTransition === "function") {
        try {
          transition = document.startViewTransition(() => {
            if (!cancelled) flushSync(() => commit());
          });
          // A newer navigation can skip a snapshot; it is not a page error.
          void transition.ready.catch(() => undefined);
          void transition.finished.catch(() => undefined);
          return;
        } catch {
          // Older browser implementations can refuse a snapshot. Fade normally.
        }
      }

      setRoutePhase("leaving");
      timer = window.setTimeout(() => {
        if (cancelled) return;
        if (reducedMotion()) { commit(); return; }
        flushSync(() => commit("arriving"));
        frame = requestAnimationFrame(() => {
          frame = requestAnimationFrame(() => { if (!cancelled) setRoutePhase("idle"); });
        });
      }, 170);
    };
    void navigate();
    return () => {
      cancelled = true;
      transition?.skipTransition();
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [requestedLocation]);

  return (
    <Routes location={displayedLocation}>
      <Route element={<Layout routePhase={routePhase} onRouteIntent={preloadPage} />}>
        <Route path="/" element={<Landing />} />
        <Route path="/how-it-works" element={<DeferredPage pathname="/how-it-works" />} />
        <Route path="/strategies" element={<DeferredPage pathname="/strategies" />} />
        <Route path="/tokens" element={<DeferredPage pathname="/tokens" />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
